import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonText, SkeletonCard, SkeletonAvatar } from '@/components/ui/skeleton'
import { LoadingSpinner as UILoadingSpinner, LoadingOverlay, LoadingButton } from '@/components/ui/loading-spinner'
import LoadingSpinner from '@/components/LoadingSpinner'
import { OptimizedImage, ResponsiveImage } from '@/components/ui/optimized-image'
import { useDebounce } from '@/hooks/use-debounce'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { measurePerformance, measureAsyncPerformance } from '@/utils/performance'
import { apiClient } from '@/services/api-client'
import { toast } from '@/components/ui/sonner'

const LoadingTestPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [testData, setTestData] = useState<any[]>([])
  const [showSkeletons, setShowSkeletons] = useState(false)
  const [imageLoadTest, setImageLoadTest] = useState(false)
  
  // Test custom hooks
  const debouncedSearch = useDebounce(searchTerm, 500)
  const [testStorage, setTestStorage] = useLocalStorage('test-storage', 'default value')
  const { ref: intersectionRef, isIntersecting, hasIntersected } = useIntersectionObserver()

  // Test performance monitoring
  const testPerformance = () => {
    measurePerformance('Test Operation', () => {
      // Simulate heavy operation
      let result = 0
      for (let i = 0; i < 1000000; i++) {
        result += Math.random()
      }
      console.log('Performance test result:', result)
    })
  }

  const testAsyncPerformance = async () => {
    const { result, duration } = await measureAsyncPerformance('Async Test', async () => {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return 'Async operation completed'
    })
    console.log('Async performance result:', result, 'Duration:', duration)
  }

  // Test API client
  const testApiClient = async () => {
    try {
      setIsLoading(true)
      const result = await apiClient.get('/api/test-endpoint')
      toast.success('API test successful')
      console.log('API test result:', result)
    } catch (error) {
      toast.error('API test failed - this is expected for test endpoint')
      console.log('API test error (expected):', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Test image loading
  const testImageLoading = () => {
    setImageLoadTest(true)
    setTimeout(() => setImageLoadTest(false), 3000)
  }

  // Test skeleton loading
  const testSkeletonLoading = () => {
    setShowSkeletons(true)
    setTimeout(() => setShowSkeletons(false), 3000)
  }

  // Test intersection observer
  useEffect(() => {
    if (hasIntersected) {
      console.log('Intersection observer triggered!')
      toast.info('Element came into view')
    }
  }, [hasIntersected])

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            🏊‍♂️ SaunaDip Brand Loading Spinner Test
          </h1>
          <p className="text-gray-600 mb-8">
            This page tests all the improvements we've implemented. Use the buttons below to test different features.
          </p>

          {/* Test Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Button 
              onClick={() => {
                testPerformance();
                toast.success('Performance test completed! Check console for results.');
              }} 
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              🚀 Test Performance Monitoring
            </Button>
            
            <Button 
              onClick={async () => {
                await testAsyncPerformance();
                toast.success('Async performance test completed!');
              }} 
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              ⏱️ Test Async Performance
            </Button>
            
            <Button 
              onClick={testApiClient} 
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              🌐 Test API Client
            </Button>
            
            <Button 
              onClick={() => {
                testImageLoading();
                toast.info('Image loading test started - check below for results');
              }} 
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              🖼️ Test Image Loading
            </Button>
            
            <Button 
              onClick={() => {
                testSkeletonLoading();
                toast.info('Skeleton loading test started - check below for results');
              }} 
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              💀 Test Skeleton Loading
            </Button>
            
            <Button 
              onClick={() => {
                const newValue = `Updated at ${new Date().toLocaleTimeString()}`;
                setTestStorage(newValue);
                toast.success(`Local storage updated: ${newValue}`);
              }}
              variant="outline"
              className="hover:bg-sauna-orange/10"
            >
              💾 Test Local Storage
            </Button>
          </div>

          {/* Current Values Display */}
          <div className="bg-gray-100 p-4 rounded-lg mb-8">
            <h3 className="font-semibold mb-2">Current Test Values:</h3>
            <div className="space-y-2 text-sm">
              <div><strong>Search Term:</strong> {searchTerm}</div>
              <div><strong>Debounced Search:</strong> {debouncedSearch}</div>
              <div><strong>Local Storage Value:</strong> {testStorage}</div>
              <div><strong>Is Intersecting:</strong> {isIntersecting ? 'Yes' : 'No'}</div>
              <div><strong>Has Intersected:</strong> {hasIntersected ? 'Yes' : 'No'}</div>
            </div>
          </div>

          {/* Search Input for Debounce Test */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Debounce Hook:
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to test debounce..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sauna-orange"
            />
          </div>

          {/* Loading Spinners Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Loading Spinners Comparison</h3>
            
            {/* New Standardized Loading Spinner */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-green-600">✅ New Standardized Loading Spinner (Recommended)</h4>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <LoadingSpinner size="sm" />
                  <p className="text-xs mt-1">Small</p>
                </div>
                <div className="text-center">
                  <LoadingSpinner size="md" />
                  <p className="text-xs mt-1">Medium</p>
                </div>
                <div className="text-center">
                  <LoadingSpinner size="lg" />
                  <p className="text-xs mt-1">Large</p>
                </div>
                <div className="text-center">
                  <LoadingSpinner size="xl" />
                  <p className="text-xs mt-1">Extra Large</p>
                </div>
                <div className="text-center">
                  <LoadingSpinner size="lg" text="Loading..." />
                  <p className="text-xs mt-1">With Text</p>
                </div>
              </div>
            </div>

            {/* Old UI Loading Spinner */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-orange-600">⚠️ Old UI Loading Spinner (Legacy)</h4>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <UILoadingSpinner size="sm" />
                  <p className="text-xs mt-1">Small</p>
                </div>
                <div className="text-center">
                  <UILoadingSpinner size="md" />
                  <p className="text-xs mt-1">Medium</p>
                </div>
                <div className="text-center">
                  <UILoadingSpinner size="lg" />
                  <p className="text-xs mt-1">Large</p>
                </div>
                <div className="text-center">
                  <UILoadingSpinner size="xl" />
                  <p className="text-xs mt-1">Extra Large</p>
                </div>
                <div className="text-center">
                  <UILoadingSpinner variant="primary" />
                  <p className="text-xs mt-1">Primary</p>
                </div>
                <div className="text-center">
                  <UILoadingSpinner variant="secondary" />
                  <p className="text-xs mt-1">Secondary</p>
                </div>
              </div>
            </div>

            {/* Usage Guidelines */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h5 className="font-medium text-blue-800 mb-2">📋 Loading Spinner Guidelines</h5>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• <strong>Use New Standardized Spinner:</strong> Import from '@/components/LoadingSpinner'</p>
                <p>• <strong>Consistent Branding:</strong> Orange color (text-sauna-orange)</p>
                <p>• <strong>Simple Implementation:</strong> Uses Loader2 icon from Lucide React</p>
                <p>• <strong>Text Support:</strong> Optional loading text with proper styling</p>
                <p>• <strong>Size Options:</strong> sm, md, lg, xl</p>
              </div>
            </div>
          </div>

          {/* Full Page Loading Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Full Page Loading Test</h3>
            <p className="text-gray-700 mb-4">Test the full page loading effect that appears during app initialization:</p>
            <Button 
              onClick={() => {
                // Simulate full page loading by showing the loading screen
                const loadingElement = document.createElement('div');
                loadingElement.id = 'full-page-loading-test';
                loadingElement.className = 'fixed inset-0 z-50 flex items-center justify-center bg-white';
                loadingElement.innerHTML = `
                  <div class="text-center space-y-6">
                    <div class="relative">
                      <div class="relative flex justify-center">
                        <div class="relative w-16 h-16">
                          <div class="absolute inset-0 rounded-full border-2 border-gray-200"></div>
                          <div class="absolute inset-0 rounded-full border-2 border-transparent border-t-sauna-orange animate-spin"></div>
                        </div>
                      </div>
                    </div>
                    <div class="relative">
                      <p class="text-sauna-navy font-semibold text-lg tracking-wide">Warming up the sauna...</p>
                    </div>
                    <div class="flex justify-center space-x-1">
                      <div class="w-1.5 h-1.5 bg-sauna-orange rounded-full animate-pulse"></div>
                      <div class="w-1.5 h-1.5 bg-sauna-orange rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
                      <div class="w-1.5 h-1.5 bg-sauna-orange rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
                    </div>
                  </div>
                `;
                document.body.appendChild(loadingElement);
                
                // Remove after 3 seconds
                setTimeout(() => {
                  const element = document.getElementById('full-page-loading-test');
                  if (element) {
                    element.remove();
                  }
                }, 3000);
              }}
              className="bg-sauna-orange text-white px-6 py-3 rounded-lg hover:bg-sauna-orange/90 transition-colors"
            >
              🚀 Test Full Page Loading
            </Button>
          </div>

          {/* Skeleton Components Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Skeleton Components</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium mb-2">Text Skeleton</h4>
                <SkeletonText lines={3} />
              </div>
              <div>
                <h4 className="font-medium mb-2">Card Skeleton</h4>
                <SkeletonCard />
              </div>
              <div>
                <h4 className="font-medium mb-2">Avatar Skeleton</h4>
                <div className="flex gap-2">
                  <SkeletonAvatar size="sm" />
                  <SkeletonAvatar size="md" />
                  <SkeletonAvatar size="lg" />
                </div>
              </div>
            </div>
          </div>

          {/* Loading Button Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Loading Button</h3>
            <p className="text-gray-600 mb-4">Click the button below to test the loading button component:</p>
            <div className="space-y-4">
              <LoadingButton 
                loading={isLoading}
                onClick={() => {
                  setIsLoading(true);
                  toast.info('Loading button test started...');
                  setTimeout(() => {
                    setIsLoading(false);
                    toast.success('Loading button test completed!');
                  }, 2000);
                }}
                className="bg-sauna-orange text-white px-6 py-2 rounded hover:bg-sauna-orange/90"
              >
                Test Loading Button (2s)
              </LoadingButton>
              
              <div className="text-sm text-gray-500">
                <p>• Shows loading spinner while processing</p>
                <p>• Disables button during loading</p>
                <p>• Provides visual feedback to users</p>
              </div>
            </div>
          </div>

          {/* Image Loading Test */}
          {imageLoadTest && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Image Loading Test</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Optimized Image</h4>
                  <OptimizedImage
                    src="https://picsum.photos/400/300"
                    alt="Test image"
                    className="w-full h-48 object-cover rounded"
                  />
                </div>
                <div>
                  <h4 className="font-medium mb-2">Responsive Image</h4>
                  <ResponsiveImage
                    src="https://picsum.photos/400/300"
                    alt="Test responsive image"
                    aspectRatio="4/3"
                    className="rounded"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Skeleton Loading Test */}
          {showSkeletons && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Skeleton Loading Test</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-32 w-full" />
                    <SkeletonText lines={2} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Intersection Observer Test */}
          <div 
            ref={intersectionRef}
            className="bg-white rounded-lg shadow p-6 mb-8 border-2 border-dashed border-gray-300"
          >
            <h3 className="text-lg font-semibold mb-4">Intersection Observer Test</h3>
            <p className="text-gray-600">
              This element will trigger a toast notification when it comes into view.
              Scroll down to see it in action!
            </p>
            <div className="mt-4 p-4 bg-blue-50 rounded">
              <p className="text-sm text-blue-800">
                <strong>Status:</strong> {isIntersecting ? 'In View' : 'Not in View'}
              </p>
              <p className="text-sm text-blue-800">
                <strong>Has Intersected:</strong> {hasIntersected ? 'Yes' : 'No'}
              </p>
            </div>
          </div>

          {/* Loading Overlay Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Loading Overlay Test</h3>
            <p className="text-gray-600 mb-4">Test the loading overlay that covers content during operations:</p>
            <LoadingOverlay isLoading={isLoading}>
              <div className="p-8 bg-gray-100 rounded text-center">
                <h4 className="font-semibold text-lg mb-4">Sample Content</h4>
                <p className="text-gray-600 mb-4">
                  This content will be covered by a loading overlay when the button is clicked.
                  The overlay prevents user interaction while showing a loading spinner.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>• Covers entire content area</p>
                  <p>• Prevents user interaction</p>
                  <p>• Shows loading spinner</p>
                </div>
                <Button 
                  onClick={() => {
                    setIsLoading(true);
                    toast.info('Loading overlay test started...');
                    setTimeout(() => {
                      setIsLoading(false);
                      toast.success('Loading overlay test completed!');
                    }, 2000);
                  }}
                  className="mt-4 bg-sauna-orange text-white hover:bg-sauna-orange/90"
                >
                  Show Loading Overlay (2s)
                </Button>
              </div>
            </LoadingOverlay>
          </div>

          {/* Error Boundary Test */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">Error Boundary Test</h3>
            <p className="text-gray-600 mb-4">
              Click the button below to test the error boundary by throwing an error.
              This will trigger the global error boundary and show the error page.
            </p>
            <div className="space-y-4">
              <Button 
                onClick={() => {
                  toast.error('Throwing test error...');
                  setTimeout(() => {
                    throw new Error('Test error from LoadingTestPage - This is expected behavior for testing error boundaries');
                  }, 1000);
                }}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
              🚨 Throw Test Error
              </Button>
              
              <div className="text-sm text-gray-500">
                <p>• Tests global error boundary</p>
                <p>• Shows error page when triggered</p>
                <p>• Helps catch and handle errors gracefully</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoadingTestPage 